import {
  ChevronDown,
  EyeIcon,
  FlaskConical,
  Loader2,
  Maximize2Icon,
  MessageSquarePlusIcon,
  XIcon,
} from 'lucide-react'
import { type FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { AgentThread } from '@/components/agent/AgentThread'
import { ThreadContextIndicator } from '@/components/agent/ThreadContextIndicator'
import type { GraphAction } from '@/components/langgraph-chat/types'
import { useAgentSidebar } from '@/components/layout/AgentSidebarContext'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { AgentChatHost, type AgentScope } from './AgentChatHost'
import { promptNeedsStandardChat } from './assistant-prompt'
import { SidebarPromptDraft } from './SidebarPromptDraft'
import { useThreadList } from './useAgentRuntime'
import { useLakeThreadList } from './useLakeAgentRuntime'
import { useSidebarThread } from './useSidebarThread'

export interface AgentSidebarProps {
  scope?: AgentScope
  caseId?: string
  currentView?: string
  onClose: () => void
  onGraphAction?: (action: GraphAction) => void
}

/**
 * The assistant panel. It is made anew for another scope or case: the thread it talks in, the
 * list that thread was taken from and a start that failed all belong to that one.
 */
export const AgentSidebar: FC<AgentSidebarProps> = props => (
  <AgentSidebarPanel key={`${props.scope ?? 'case'}:${props.caseId ?? ''}`} {...props} />
)

const AgentSidebarPanel: FC<AgentSidebarProps> = ({
  scope = 'case',
  caseId,
  currentView,
  onClose,
  onGraphAction,
}) => {
  const { t } = useTranslation()
  const { setOpen, requestedThreadId, consumeThreadRequest, requestedPrompt } = useAgentSidebar()
  const navigate = useNavigate()
  const { caseId: routeCaseId } = useParams<{ caseId: string }>()
  const targetCaseId = routeCaseId ?? caseId

  // Sidebar shows ALL threads in one dropdown, grouped by kind. Both kinds
  // can be loaded into the local AgentChatHost — clicking a hypothesis-run
  // thread shows the autonomous run's full conversation (tools + assistant
  // messages) so the user can ask follow-ups. The hypothesis detail page
  // is reachable separately via the breadcrumb.
  // Lake scope has no hypothesis runs — the corresponding list is always
  // empty when scope='lake' and the hypothesis section is hidden.
  const caseList = useThreadList(scope === 'case' && caseId ? caseId : '')
  const lakeList = useLakeThreadList()
  const list = scope === 'lake' ? lakeList : caseList
  const { threads, create } = list
  const chatThreads =
    scope === 'lake'
      ? threads.map(t => ({ ...t, kind: 'chat' as const, hypothesis_id: null }))
      : caseList.threads.filter(thread => thread.kind === 'chat')
  const hypothesisThreads =
    scope === 'lake' ? [] : caseList.threads.filter(thread => thread.kind === 'hypothesis_run')
  // The latest CHAT thread once the list is there, or a first one made for a
  // user who has none: the composer never shows without a thread. Hypothesis
  // runs are not picked because the user usually wants to start in chat.
  const { activeThreadId, setActiveThreadId, failed, retry } = useSidebarThread({
    chatThreads,
    isLoading: list.isLoading,
    createThread: () => create.mutateAsync(),
  })

  // External "Open thread" requests (e.g. from the hypothesis detail page)
  // take priority over the auto-select. We consume the request immediately
  // so re-opening the sidebar later doesn't re-jump to the same thread.
  useEffect(() => {
    if (requestedThreadId) {
      setActiveThreadId(requestedThreadId)
      consumeThreadRequest()
    }
  }, [requestedThreadId, consumeThreadRequest, setActiveThreadId])

  // Look up across BOTH kinds — the active thread can be a hypothesis run
  // when triggered via openWithThread.
  const activeThread = threads.find(t => t.id === activeThreadId)
  const activeKind =
    scope === 'lake' ? 'chat' : caseList.threads.find(t => t.id === activeThreadId)?.kind
  const needsStandardChat = promptNeedsStandardChat(requestedPrompt?.caseId, caseId, activeKind)
  const latestChatId = chatThreads[0]?.id
  useEffect(() => {
    if (needsStandardChat) setActiveThreadId(latestChatId)
  }, [needsStandardChat, latestChatId, setActiveThreadId])

  async function handleNewThread() {
    const thread = await create.mutateAsync()
    setActiveThreadId(thread.id)
  }

  function handleMaximize() {
    setOpen(false)
    const params = activeThreadId ? `?thread=${activeThreadId}` : ''
    if (scope === 'lake') {
      // No standalone /documents/agent route in v1 — close + keep the
      // sidebar collapse behaviour consistent with the case sidebar.
      return
    }
    if (targetCaseId) navigate(`/cases/${targetCaseId}/agent${params}`)
  }

  const header = (
    <div className="flex items-center gap-1 border-b pl-4 pr-2 py-1.5 shrink-0 md:px-2">
      {/* Thread selector */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex min-w-0 items-center gap-1 text-sm text-foreground hover:text-foreground transition-colors max-md:h-9 md:max-w-[180px] md:text-xs"
          >
            <span className="font-medium truncate">
              {activeThread?.title ?? t('agentSidebar.newConversation')}
            </span>
            <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72 max-w-[calc(100vw-2rem)]">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {t('agentSidebar.threads')}
          </DropdownMenuLabel>
          {chatThreads.length === 0 ? (
            <div className="px-2 py-1.5 text-xs text-muted-foreground/70">
              {t('agentSidebar.noThreadsYet')}
            </div>
          ) : (
            chatThreads.map(thread => (
              <DropdownMenuItem
                key={thread.id}
                onSelect={() => setActiveThreadId(thread.id)}
                className="text-xs max-md:py-2.5 max-md:text-sm"
              >
                <span className="truncate">
                  {thread.title ?? t('agentSidebar.newConversation')}
                </span>
                {thread.id === activeThreadId && (
                  <span className="ml-auto text-muted-foreground">{t('agentSidebar.current')}</span>
                )}
              </DropdownMenuItem>
            ))
          )}
          {scope === 'case' && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {t('agentSidebar.hypothesisRuns')}
              </DropdownMenuLabel>
              {hypothesisThreads.length === 0 ? (
                <div className="px-2 py-1.5 text-xs text-muted-foreground/70">
                  {t('agentSidebar.noHypothesisRuns')}
                </div>
              ) : (
                hypothesisThreads.map(thread => (
                  <DropdownMenuItem
                    key={thread.id}
                    onSelect={() => setActiveThreadId(thread.id)}
                    className="text-xs max-md:py-2.5 max-md:text-sm"
                  >
                    <FlaskConical className="size-3 mr-1.5 shrink-0 text-primary" />
                    <span className="truncate">
                      {thread.title ?? t('agentSidebar.hypothesisRun')}
                    </span>
                    {thread.id === activeThreadId && (
                      <span className="ml-auto text-muted-foreground">
                        {t('agentSidebar.current')}
                      </span>
                    )}
                  </DropdownMenuItem>
                ))
              )}
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={handleNewThread}
            className="text-xs max-md:py-2.5 max-md:text-sm"
          >
            <MessageSquarePlusIcon className="size-3.5 mr-2" />
            {t('agentSidebar.newThread')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="flex-1" />

      <Button
        variant="ghost"
        size="icon"
        className="size-9 lg:size-6"
        onClick={handleMaximize}
        title={t('agentSidebar.openFullPage')}
      >
        <Maximize2Icon className="size-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="size-9 lg:size-6"
        onClick={onClose}
        title={t('agentSidebar.close')}
      >
        <XIcon className="size-3.5" />
      </Button>
    </div>
  )

  return (
    // An overlay until there is room for a column beside the page: full-screen
    // below md, docked right from md, and from lg the 480 px column in the flow.
    <aside className="fixed inset-0 z-40 flex h-dvh w-full shrink-0 flex-col border-border bg-background overflow-hidden shadow-[-8px_0_24px_-12px_rgb(0_0_0/0.08)] md:left-auto md:w-[480px] md:border-l lg:static lg:z-auto lg:h-full">
      {header}
      <div className="min-h-0 flex-1">
        {activeThreadId ? (
          <AgentChatHost
            key={activeThreadId}
            scope={scope}
            caseId={scope === 'case' ? caseId : undefined}
            threadId={activeThreadId}
            currentView={currentView}
            onGraphAction={onGraphAction}
          >
            <SidebarPromptDraft
              caseId={scope === 'case' ? caseId : undefined}
              thread={{ id: activeThreadId, ready: activeKind === 'chat' }}
            />
            <AgentThread
              background="background"
              composerFooter={
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-1 text-[10.5px] text-muted-foreground max-md:justify-end max-md:gap-y-0.5">
                  {scope === 'lake' ? (
                    <span className="inline-flex items-center gap-1">
                      <EyeIcon className="size-3" />
                      {t('agentSidebar.context', { view: 'Data Lake' })}
                    </span>
                  ) : (
                    currentView && (
                      <span className="inline-flex items-center gap-1">
                        <EyeIcon className="size-3" />
                        {t('agentSidebar.context', { view: currentView })}
                      </span>
                    )
                  )}
                  {scope === 'case' && (
                    <span className="ml-auto">
                      <ThreadContextIndicator />
                    </span>
                  )}
                </div>
              }
            />
          </AgentChatHost>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-sm text-muted-foreground">
            {failed ? (
              <>
                <p>{t('agentSidebar.startFailed')}</p>
                <Button variant="outline" size="sm" onClick={() => void retry()}>
                  {t('chat.retryFailedSend')}
                </Button>
              </>
            ) : (
              <Loader2 className="size-4 animate-spin" aria-label={t('common.loading')} />
            )}
          </div>
        )}
      </div>
    </aside>
  )
}
